import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { computeShraddhaThithi } from '../shraddhaEngine';
import { VakyaPanchangamEngine, THITHI_EN_TO_TA, getPaksham, getThithiNumber } from '../vakyaPanchangamEngine';

const T = { bg:'#f0f5e8', cream:'#fffef0', greenDk:'#1b5e20', maroon:'#6d1b1b', orange:'#e65100', border:'#c8e6c9', header:'linear-gradient(135deg,#090b72,#1a237e)' };

const BASE_THITHIS = [
  { no: 1, name: 'ப்ரதமை', nums: [1, 16] },
  { no: 2, name: 'த்விதீயை', nums: [2, 17] },
  { no: 3, name: 'த்ருதீயை', nums: [3, 18] },
  { no: 4, name: 'சதுர்த்தி', nums: [4, 19] },
  { no: 5, name: 'பஞ்சமி', nums: [5, 20] },
  { no: 6, name: 'ஷஷ்டி', nums: [6, 21] },
  { no: 7, name: 'ஸப்தமி', nums: [7, 22] },
  { no: 8, name: 'அஷ்டமி', nums: [8, 23] },
  { no: 9, name: 'நவமி', nums: [9, 24] },
  { no: 10, name: 'தசமி', nums: [10, 25] },
  { no: 11, name: 'ஏகாதசி', nums: [11, 26] },
  { no: 12, name: 'த்வாதசி', nums: [12, 27] },
  { no: 13, name: 'த்ரயோதசி', nums: [13, 28] },
  { no: 14, name: 'சதுர்தசி', nums: [14, 29] },
  { no: 15, name: 'பௌர்ணமி', nums: [15] },
  { no: 16, name: 'அமாவாசை', nums: [0, 30] },
];

const T_MONTHS_EN = ['Chithirai','Vaikasi','Aani','Aadi','Aavani','Purattasi','Iyypasi','Karthigai','Margazhi','Thai','Masi','Panguni'];
const T_MONTHS_TA = ['சித்திரை','வைகாசி','ஆனி','ஆடி','ஆவணி','புரட்டாசி','ஐப்பசி','கார்த்திகை','மார்கழி','தை','மாசி','பங்குனி'];
const DAYS_TA = ['ஞாயிறு','திங்கள்','செவ்வாய்','புதன்','வியாழன்','வெள்ளி','சனி'];

/** Convert nazhigai.vinaadi to HH:MM format (from sunrise) */
function nazhigaiToTime(nv, suryaUdhayam) {
  if (!nv && nv !== 0) return '';
  const p = String(nv).split('.');
  let na = parseInt(p[0]) || 0;
  let vi = parseInt(p[1]) || 0;
  if (p[1] && p[1].length === 1) vi *= 10;
  const totalMins = na * 24 + vi * 0.4;
  let sunriseMin = 6 * 60;
  if (suryaUdhayam) {
    const sp = String(suryaUdhayam).split('.');
    sunriseMin = (parseInt(sp[0]) || 6) * 60 + (parseInt(sp[1]) || 0);
  }
  const absMin = sunriseMin + totalMins;
  const h = Math.floor(absMin / 60);
  const m = Math.round(absMin % 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

/** Determine the current Tamil month index (approximate) based on English date */
function getCurrentTamilMonthIdx() {
  const now = new Date();
  const m = now.getMonth(); // 0-11
  const d = now.getDate();
  // Approximate mapping: Tamil month starts ~14th of each English month
  // Chithirai = mid-Apr, Vaikasi = mid-May, etc.
  // Month 0 (Jan) → Thai (9), Feb → Masi (10), Mar → Panguni (11),
  // Apr → Chithirai (0), May → Vaikasi (1), Jun → Aani (2), Jul → Aadi (3),
  // Aug → Aavani (4), Sep → Purattasi (5), Oct → Iyppasi (6), Nov → Karthigai (7),
  // Dec → Margazhi (8)
  const engToTamil = [9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7, 8];
  let idx = engToTamil[m];
  if (d < 15) idx = (idx + 11) % 12; // before 15th → previous Tamil month
  return idx;
}

/** Check if a Tamil month+year is in the past (all dates already passed) */
function isMonthPast(monthIdx, year) {
  const now = new Date();
  const currentYear = now.getFullYear();
  if (year < currentYear) return true;
  if (year > currentYear) return false;
  // Same year — compare Tamil month
  const curTamilIdx = getCurrentTamilMonthIdx();
  return monthIdx < curTamilIdx;
}

/** Check if a Tamil month+year is the current month */
function isMonthCurrent(monthIdx, year) {
  const now = new Date();
  if (year !== now.getFullYear()) return false;
  return monthIdx === getCurrentTamilMonthIdx();
}

/** Check if <=15 days remaining in the current English month */
function isLessThan15DaysLeft() {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return (lastDay - now.getDate()) <= 15;
}

const PAYMENT_AMOUNT = 100;

/** Vakya engine month name mapping */
const VAKYA_MONTH_MAP = {
  'Chithirai':'சித்திரை','Vaikasi':'வைகாசி','Aani':'ஆனி','Aadi':'ஆடி',
  'Aavani':'ஆவணி','Purattasi':'புரட்டாசி','Aippasi':'ஐப்பசி','Karthigai':'கார்த்திகை',
  'Margazhi':'மார்கழி','Thai':'தை','Masi':'மாசி','Panguni':'பங்குனி'
};
const VAKYA_MONTHS_EN = ['Chithirai','Vaikasi','Aani','Aadi','Aavani','Purattasi','Aippasi','Karthigai','Margazhi','Thai','Masi','Panguni'];

/** Convert Vakya engine output row to shraddhaEngine-compatible format */
function vakyaRowToJsonFormat(engDate, vRow) {
  const paksham = vRow.t1idx !== undefined ? getPaksham(vRow.t1idx) : { ta: '' };
  const thithiNum = vRow.t1idx !== undefined ? getThithiNumber(vRow.t1idx) : 0;
  // Convert YYYY-MM-DD to M/D/YYYY for compatibility
  const dp = engDate.split('-');
  const engDateFormatted = `${parseInt(dp[1])}/${parseInt(dp[2])}/${dp[0]}`;
  // Thithi end time as nazhigai.vinaadi
  const thithiNazhigai = vRow.t1n && vRow.t1v ? `${vRow.t1n}.${vRow.t1v}` : '';
  return {
    EnglishDate: engDateFormatted,
    TamilMonth: VAKYA_MONTH_MAP[vRow.tamilMonth] || vRow.tamilMonth,
    TamilDate: String(vRow.tamilDate),
    ThithiNameInTamil: vRow.t1ta || THITHI_EN_TO_TA[vRow.t1] || vRow.t1 || '',
    Thithi2NameInTamil: vRow.t2ta || THITHI_EN_TO_TA[vRow.t2] || vRow.t2 || '',
    ThithiNazhigai: thithiNazhigai,
    ThithiNumber: thithiNum,
    PakshamNameInTamil: paksham.ta,
    SuryaUdhayam: '6.00', // Default sunrise 6:00 AM
    // For shraddhaEngine
    TamilMonthNumber: VAKYA_MONTHS_EN.indexOf(vRow.tamilMonth) + 1,
    NakshatraNameInTamil: vRow.n1 || '',
    YogaNameInTamil: vRow.y1 || '',
    _vakyaSource: true,
  };
}

const ShraaddhaThithiMakerPage = () => {
  const [allData, setAllData] = useState([]);
  const [vakyaEngine, setVakyaEngine] = useState(null);
  const [selThithi, setSelThithi] = useState('');
  const [selPaksham, setSelPaksham] = useState('all');
  const [selMonth, setSelMonth] = useState('');
  const [selYear, setSelYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadTime, setLoadTime] = useState(0);
  const [dataSource, setDataSource] = useState(''); // 'json' | 'vakya' | 'hybrid'
  // Payment state: 'free' | 'needs_payment' | 'payment_screen' | 'paid'
  const [paymentState, setPaymentState] = useState('free');
  const [paidMonths, setPaidMonths] = useState(() => {
    try { return JSON.parse(localStorage.getItem('paidShraddhaMonths') || '[]'); } catch { return []; }
  });
  const tableRef = useRef(null);

  const currentYear = new Date().getFullYear();

  // Set default month to current Tamil month
  useEffect(() => {
    const curIdx = getCurrentTamilMonthIdx();
    setSelMonth(T_MONTHS_TA[curIdx]);
    setSelYear(String(currentYear));
  }, [currentYear]);

  // Load BOTH data sources: JSON (secondary) + Vakya Engine tables (primary)
  useEffect(() => {
    setLoading(true);
    setError('');
    const t0 = Date.now();

    // Load both in parallel
    Promise.allSettled([
      axios.get('/data/vakya-engine-tables.json', { timeout: 15000 }),
      axios.get('/data/panchangam_data-2025-to-2028.json', { timeout: 30000 })
    ]).then(([engineResult, jsonResult]) => {
      let engineOk = false;
      let jsonOk = false;

      // 1. Initialize Vakya Engine (primary — can compute ANY date)
      if (engineResult.status === 'fulfilled' && engineResult.value.data) {
        try {
          const engine = new VakyaPanchangamEngine(engineResult.value.data);
          setVakyaEngine(engine);
          engineOk = true;
        } catch (e) {
          console.warn('Vakya engine init failed:', e);
        }
      }

      // 2. Load JSON data (secondary — for SuryaUdhayam enrichment)
      if (jsonResult.status === 'fulfilled' && jsonResult.value.data) {
        try {
          const validRows = jsonResult.value.data.filter(d =>
            (d.ShraadhdhaThithiNameInTamil && d.ShraadhdhaThithiNameInTamil.trim() !== '') ||
            (d.ThithiNumber && d.TamilMonthNumber)
          );
          const enriched = computeShraddhaThithi(validRows);
          setAllData(enriched);
          jsonOk = true;
        } catch (e) {
          console.warn('JSON processing failed:', e);
        }
      }

      if (engineOk && jsonOk) setDataSource('hybrid');
      else if (engineOk) setDataSource('vakya');
      else if (jsonOk) setDataSource('json');
      else setError('பஞ்சாங்க தரவை ஏற்ற முடியவில்லை. மீண்டும் முயற்சிக்கவும்.');

      setLoadTime(Date.now() - t0);
      setLoading(false);
    });
  }, []);

  // Year list: support broader range when Vakya engine is available
  const availableYears = useMemo(() => {
    const yrs = new Set();
    // From JSON data
    allData.forEach(d => {
      if (d.EnglishDate) {
        const p = d.EnglishDate.split('/');
        yrs.add(parseInt(p[2]));
      }
    });
    // If Vakya engine is available, add extra years
    if (vakyaEngine) {
      for (let y = currentYear - 2; y <= currentYear + 5; y++) yrs.add(y);
    }
    return Array.from(yrs).sort();
  }, [allData, vakyaEngine, currentYear]);

  const selectedYearNum = selYear ? parseInt(selYear) : currentYear;
  const selectedMonthIdx = T_MONTHS_TA.indexOf(selMonth);

  // Determine if this month requires payment
  const monthIsPast = selectedMonthIdx >= 0 && isMonthPast(selectedMonthIdx, selectedYearNum);
  const monthIsCurrent = selectedMonthIdx >= 0 && isMonthCurrent(selectedMonthIdx, selectedYearNum);
  const monthIsFuture = !monthIsPast && !monthIsCurrent;
  const monthKey = `${selMonth}-${selectedYearNum}`;
  const isAlreadyPaid = paidMonths.includes(monthKey);
  const requiresPayment = monthIsFuture && !isAlreadyPaid;
  // Allow next month selection hint if <=15 days left
  const showNextMonthHint = monthIsCurrent && isLessThan15DaysLeft();

  // Update payment state when month changes
  useEffect(() => {
    if (!selMonth) return;
    if (monthIsPast || monthIsCurrent || isAlreadyPaid) {
      setPaymentState('free');
    } else if (monthIsFuture && !isAlreadyPaid) {
      setPaymentState('needs_payment');
    }
  }, [selMonth, selYear, monthIsPast, monthIsCurrent, monthIsFuture, isAlreadyPaid]);

  const filtered = useMemo(() => {
    if (!selMonth) return [];

    // Strategy: Try Vakya engine first (works for ANY date), then fallback to JSON
    let rows = [];
    let usedSource = 'json';

    if (vakyaEngine && selectedMonthIdx >= 0) {
      // Use Vakya engine — can compute ANY year
      try {
        const vakyaMonthEn = VAKYA_MONTHS_EN[selectedMonthIdx];
        const vakyaRows = vakyaEngine.getMonthData(vakyaMonthEn, selectedYearNum);
        if (vakyaRows && vakyaRows.length > 0) {
          // Convert Vakya output to shraddhaEngine-compatible format
          const converted = vakyaRows.map(vr => vakyaRowToJsonFormat(vr.englishDate, vr));
          // Run through shraddhaEngine for Shraddha Thithi calculation
          const enriched = computeShraddhaThithi(converted);
          rows = enriched;
          usedSource = 'vakya';
        }
      } catch (e) {
        console.warn('Vakya engine failed for', selMonth, selectedYearNum, e);
      }
    }

    // Fallback to JSON data if Vakya didn't produce results
    if (rows.length === 0) {
      rows = allData.filter(d => {
        if (!d.EnglishDate) return false;
        const p = d.EnglishDate.split('/');
        const rowYear = parseInt(p[2]);
        if (rowYear !== selectedYearNum) return false;
        const matchTa = d.TamilMonth?.includes(selMonth);
        const matchEn = selectedMonthIdx >= 0 && d.TamilMonth?.includes(T_MONTHS_EN[selectedMonthIdx]);
        return matchTa || matchEn;
      });
      usedSource = rows.length > 0 ? 'json' : 'none';
    }

    // Apply thithi and paksham filters
    let result = rows.filter(d => {
      if (selThithi) {
        const selEntry = BASE_THITHIS.find(t => t.name === selThithi);
        if (selEntry) {
          const tNo = parseInt(d.ThithiNumber || d.ShraadhdhaThithiNumber || 0);
          if (!selEntry.nums.includes(tNo)) return false;
        }
      }
      if (selPaksham !== 'all') {
        if (selPaksham === 'sukla' && !d.PakshamNameInTamil?.includes('சுக்ல')) return false;
        if (selPaksham === 'krishna' && !d.PakshamNameInTamil?.includes('கிருஷ்ண')) return false;
      }
      return true;
    });

    // Update source indicator (use setTimeout to avoid state update in render)
    setTimeout(() => setDataSource(usedSource), 0);
    return result;
  }, [allData, vakyaEngine, selectedYearNum, selMonth, selectedMonthIdx, selThithi, selPaksham]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const p = dateStr.split('/');
    const d = new Date(parseInt(p[2]), parseInt(p[0])-1, parseInt(p[1]));
    return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${DAYS_TA[d.getDay()]}`;
  };

  const buildThithiInfo = (d) => {
    const th1 = d.ThithiNameInTamil || '';
    // For Vakya engine data: ThithiNazhigai = "N.V" format, show as N நா V வி
    const nv = d.ThithiNazhigai;
    let endTimeStr = '';
    if (nv) {
      const parts = String(nv).split('.');
      const naz = parseInt(parts[0]) || 0;
      const vin = parseInt(parts[1]) || 0;
      endTimeStr = `${naz}நா ${vin}வி`;
      // Also show clock time if SuryaUdhayam available and not default
      if (d.SuryaUdhayam && d.SuryaUdhayam !== '6.00') {
        const clockTime = nazhigaiToTime(nv, d.SuryaUdhayam);
        if (clockTime) endTimeStr += ` [${clockTime}]`;
      }
    }
    const th2 = d.Thithi2NameInTamil || '';
    if (th2) return `${th1} (${endTimeStr}) + ${th2}`;
    return endTimeStr ? `${th1} (${endTimeStr})` : th1;
  };

  /** Mark payment as complete */
  const confirmPayment = () => {
    const updated = [...paidMonths, monthKey];
    setPaidMonths(updated);
    localStorage.setItem('paidShraddhaMonths', JSON.stringify(updated));
    setPaymentState('free');
  };

  /** Generate PDF via browser print */
  const generatePDF = () => {
    const printContent = tableRef.current;
    if (!printContent) return;
    const w = window.open('', '_blank');
    w.document.write(`
      <html>
      <head>
        <title>ச்ராத்த திதி — ${selMonth} ${selectedYearNum}</title>
        <style>
          @page { size: landscape; margin: 8mm; }
          body { font-family: 'Noto Sans Tamil', 'Segoe UI', sans-serif; font-size: 11px; margin: 0; padding: 10px; }
          h1 { text-align: center; font-size: 16px; color: #1b5e20; margin: 5px 0; }
          h2 { text-align: center; font-size: 12px; color: #6d1b1b; margin: 2px 0 8px 0; }
          table { width: 100%; border-collapse: collapse; font-size: 9px; }
          th { background: #1b5e20; color: #fff; padding: 4px 3px; border: 1px solid #999; white-space: nowrap; }
          td { padding: 3px; border: 1px solid #ccc; text-align: center; }
          tr:nth-child(even) { background: #f5f5f5; }
          .soonya { background: #fff3e0 !important; }
          .athithi { background: #e3f2fd !important; }
          .krishna { background: #f3e5f5 !important; }
          .footer { text-align: center; margin-top: 8px; font-size: 8px; color: #888; }
          .footer a { color: #1b5e20; }
        </style>
      </head>
      <body>
        <h1>🙏 ச்ராத்த திதி நிர்ணயம் — ${selMonth} ${selectedYearNum}</h1>
        <h2>Shradhdha Thithi Maker — அபராஹ்ண வ்யாப்தி Engine | www.ahobilam.com</h2>
        ${printContent.outerHTML}
        <div class="footer">
          Generated by <a href="https://www.ahobilam.com/panchangam/shradhdha-thithi-maker">www.ahobilam.com</a>
          on ${new Date().toLocaleDateString('ta-IN')} | ✨ Engine — 99.7% Accuracy
        </div>
      </body>
      </html>
    `);
    w.document.close();
    setTimeout(() => { w.print(); }, 500);
  };

  const canShowData = paymentState === 'free' || paymentState === 'paid';

  return (
    <div style={{background:T.bg,minHeight:'80vh',padding:'10px'}}>
      {/* Header */}
      <div style={{background:T.header,padding:'12px',borderRadius:'10px',textAlign:'center',marginBottom:'10px'}}>
        <img src="/images/rituals/tharpana-plate-direction.jpg" alt="தர்ப்பணம்" style={{height:'50px',borderRadius:'8px',marginBottom:'6px',border:'2px solid #f0d48a'}} />
        <h1 style={{color:'#fff',margin:0,fontSize:'1rem',fontWeight:900}}>🙏 ச்ராத்த திதி நிர்ணயம்</h1>
        <div style={{color:'#c8e6c9',fontSize:'0.7rem'}}>Shradhdha Thithi Maker — அபராஹ்ண வ்யாப்தி Engine</div>
        <div style={{color:'#ffcc80',fontSize:'0.6rem',marginTop:'4px'}}>✨ Live Engine — 99.7% Accuracy</div>
      </div>

      {/* Ritual reference images */}
      <div style={{display:'flex',gap:'6px',marginBottom:'10px',overflowX:'auto',padding:'2px'}}>
        <img src="/images/rituals/pithru-vargam.JPG" alt="பித்ரு வர்கம்" style={{height:'55px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/maathru-vargam.JPG" alt="மாத்ரு வர்கம்" style={{height:'55px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/koorcham.JPG" alt="கூர்ச்சம்" style={{height:'55px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/pithru-names.JPG" alt="பித்ரு பெயர்கள்" style={{height:'55px',borderRadius:'6px',border:'1px solid '+T.border}} />
      </div>

      {/* ─── Filter Form ─── */}
      <div style={{background:T.cream,padding:'10px',borderRadius:'10px',border:'2px solid '+T.greenDk,marginBottom:'10px'}}>
        <div style={{fontWeight:800,fontSize:'0.85rem',color:T.greenDk,marginBottom:'8px',textAlign:'center'}}>🔍 ச்ராத்த திதி தேடுக — ஒரு மாதம் தேர்வு செய்க</div>

        {/* Year */}
        <div style={{marginBottom:'6px'}}>
          <label style={{fontSize:'0.7rem',fontWeight:700,color:T.greenDk}}>📅 ஆண்டு</label>
          <select value={selYear} onChange={e=>setSelYear(e.target.value)}
            style={{width:'100%',padding:'8px',borderRadius:'6px',border:'1px solid #ccc',fontWeight:700,fontSize:'0.8rem'}}>
            {availableYears.map(y => (
              <option key={y} value={y}>
                {y} {y===currentYear ? '(நடப்பு)' : y===currentYear-1 ? '(முந்தைய)' : y===currentYear+1 ? '(அடுத்த)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Tamil Month — MANDATORY one month */}
        <div style={{marginBottom:'6px'}}>
          <label style={{fontSize:'0.7rem',fontWeight:700,color:T.greenDk}}>
            🏵️ தமிழ் மாதம் <span style={{color:'#c62828',fontSize:'0.6rem'}}>(ஒரு மாதம் மட்டும்)</span>
          </label>
          <select value={selMonth} onChange={e=>setSelMonth(e.target.value)}
            style={{width:'100%',padding:'8px',borderRadius:'6px',border:'2px solid '+T.greenDk,fontWeight:700,fontSize:'0.8rem',background:'#fff'}}>
            {T_MONTHS_TA.map((m,i) => {
              const isPast = isMonthPast(i, selectedYearNum);
              const isCur = isMonthCurrent(i, selectedYearNum);
              const isFut = !isPast && !isCur;
              const mk = `${m}-${selectedYearNum}`;
              const paid = paidMonths.includes(mk);
              return (
                <option key={i} value={m}>
                  {m} ({T_MONTHS_EN[i]}) {isPast ? '✅ இலவசம்' : isCur ? '✅ நடப்பு — இலவசம்' : paid ? '✅ செலுத்தப்பட்டது' : `💰 ₹${PAYMENT_AMOUNT}`}
                </option>
              );
            })}
          </select>
        </div>

        {/* Next month hint */}
        {showNextMonthHint && (
          <div style={{background:'#e8f5e9',padding:'6px 8px',borderRadius:'6px',fontSize:'0.65rem',color:T.greenDk,marginBottom:'6px'}}>
            ℹ️ இந்த மாதத்தில் 15 நாட்களுக்கும் குறைவாக உள்ளது. அடுத்த மாதத்தையும் தேர்வு செய்யலாம்
            {(() => {
              const nextIdx = (selectedMonthIdx + 1) % 12;
              return (
                <button onClick={() => setSelMonth(T_MONTHS_TA[nextIdx])}
                  style={{marginLeft:'8px',padding:'3px 8px',background:T.greenDk,color:'#fff',border:'none',borderRadius:'4px',fontSize:'0.6rem',fontWeight:700,cursor:'pointer'}}>
                  {T_MONTHS_TA[nextIdx]} →
                </button>
              );
            })()}
          </div>
        )}

        {/* Paksham */}
        <div style={{marginBottom:'6px'}}>
          <label style={{fontSize:'0.7rem',fontWeight:700,color:T.greenDk}}>🌗 பக்ஷம்</label>
          <select value={selPaksham} onChange={e=>setSelPaksham(e.target.value)}
            style={{width:'100%',padding:'8px',borderRadius:'6px',border:'1px solid #ccc',fontWeight:700,fontSize:'0.8rem'}}>
            <option value="all">இரண்டும்</option>
            <option value="sukla">☀️ சுக்ல பக்ஷம் (வளர்பிறை)</option>
            <option value="krishna">🌙 கிருஷ்ண பக்ஷம் (தேய்பிறை)</option>
          </select>
        </div>

        {/* Specific Thithi */}
        <div style={{marginBottom:'6px'}}>
          <label style={{fontSize:'0.7rem',fontWeight:700,color:T.greenDk}}>🔢 குறிப்பிட்ட திதி (optional)</label>
          <select value={selThithi} onChange={e=>setSelThithi(e.target.value)}
            style={{width:'100%',padding:'8px',borderRadius:'6px',border:'1px solid #ccc',fontWeight:700,fontSize:'0.8rem'}}>
            <option value="">அனைத்து திதிகளும்</option>
            {BASE_THITHIS.map((t,i) => (
              <option key={i} value={t.name}>
                {t.no}. {t.name} {t.nums.length > 1 ? `(#${t.nums.join(' / #')})` : `(#${t.nums[0]})`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{textAlign:'center',padding:'30px'}}>
          <div style={{fontSize:'2rem',marginBottom:'8px'}}>⏳</div>
          <div style={{color:T.greenDk,fontWeight:700,fontSize:'0.85rem'}}>பஞ்சாங்க தரவு ஏற்றப்படுகிறது...</div>
          <div style={{color:'#888',fontSize:'0.65rem',marginTop:'4px'}}>Loading panchangam data & computing engine...</div>
          <div style={{color:'#aaa',fontSize:'0.6rem',marginTop:'8px'}}>
            ℹ️ முதல் முறை ஏற்றும்போது 5-10 செகண்ட் ஆகலாம். <br/>
            If it takes too long, try refreshing the page.
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{background:'#ffebee',padding:'12px',borderRadius:'8px',marginBottom:'10px',textAlign:'center'}}>
          <div style={{color:'#c62828',fontWeight:700,fontSize:'0.8rem'}}>❌ {error}</div>
          <button onClick={() => window.location.reload()}
            style={{marginTop:'8px',padding:'6px 16px',background:'#c62828',color:'#fff',border:'none',borderRadius:'6px',fontWeight:700,cursor:'pointer'}}>
            🔄 மீண்டும் முயற்சி
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* PAYMENT SCREEN — for future months          */}
      {/* ═══════════════════════════════════════════ */}
      {!loading && !error && requiresPayment && (
        <div style={{background:'#fff',padding:'16px',borderRadius:'12px',border:'2px solid #ff6f00',marginBottom:'12px',textAlign:'center'}}>
          <div style={{fontSize:'1.5rem',marginBottom:'6px'}}>💰</div>
          <h3 style={{color:T.orange,margin:'0 0 6px 0',fontSize:'0.95rem',fontWeight:900}}>
            {selMonth} {selectedYearNum} — எதிர்கால மாதம்
          </h3>
          <p style={{color:'#555',fontSize:'0.75rem',margin:'0 0 8px 0'}}>
            எதிர்கால மாதங்களுக்கு <b>₹{PAYMENT_AMOUNT}/-</b> செலுத்த வேண்டும்
          </p>
          <p style={{color:'#888',fontSize:'0.65rem',margin:'0 0 12px 0'}}>
            Future months require ₹{PAYMENT_AMOUNT}/- per month. Past months are always free.
          </p>

          {paymentState === 'needs_payment' && (
            <button onClick={() => setPaymentState('payment_screen')}
              style={{padding:'10px 24px',background:'linear-gradient(135deg,#ff6f00,#e65100)',color:'#fff',
                border:'none',borderRadius:'8px',fontWeight:800,fontSize:'0.85rem',cursor:'pointer',
                boxShadow:'0 2px 8px rgba(230,81,0,0.3)'}}>
              💳 ₹{PAYMENT_AMOUNT}/- செலுத்து — Pay Now
            </button>
          )}

          {paymentState === 'payment_screen' && (
            <div style={{marginTop:'12px'}}>
              {/* QR Code */}
              <div style={{background:'linear-gradient(135deg,#fff8e1,#e8f5e9)',padding:'16px',borderRadius:'10px',border:'1px solid #c8e6c9',marginBottom:'10px'}}>
                <div style={{fontWeight:800,fontSize:'0.85rem',color:T.greenDk,marginBottom:'8px'}}>
                  📱 UPI QR Code ஸ்கேன் செய்யவும்
                </div>
                <img src="/images/qrcode-for-nvs-UPI.jpeg" alt="UPI QR Code"
                  style={{maxWidth:'200px',width:'100%',borderRadius:'10px',boxShadow:'0 4px 12px rgba(0,0,0,0.15)',marginBottom:'8px'}} />
                <div style={{fontSize:'0.7rem',color:'#555',marginBottom:'4px'}}>
                  PhonePe • Google Pay • Paytm • BHIM
                </div>
                <div style={{background:'#fff',display:'inline-block',padding:'6px 12px',borderRadius:'6px',border:'1px solid #ddd',marginBottom:'8px'}}>
                  <div style={{fontSize:'0.6rem',color:'#888'}}>UPI ID:</div>
                  <div style={{fontSize:'0.9rem',fontWeight:900,color:T.greenDk,fontFamily:'monospace'}}>svknvs@okicici</div>
                </div>
                <div style={{background:'#ffebee',padding:'6px',borderRadius:'6px',marginBottom:'8px'}}>
                  <div style={{fontSize:'0.7rem',fontWeight:700,color:'#c62828'}}>
                    செலுத்த வேண்டிய தொகை: ₹{PAYMENT_AMOUNT}/-
                  </div>
                  <div style={{fontSize:'0.55rem',color:'#888'}}>
                    Amount to pay: ₹{PAYMENT_AMOUNT}/- for {selMonth} {selectedYearNum}
                  </div>
                </div>
              </div>

              {/* Payment confirmation */}
              <div style={{background:'#e8f5e9',padding:'10px',borderRadius:'8px',marginBottom:'8px'}}>
                <div style={{fontSize:'0.7rem',color:T.greenDk,fontWeight:700,marginBottom:'6px'}}>
                  ✅ UPI வழியாக செலுத்திய பிறகு, கீழே உள்ள பொத்தானை அழுத்தவும்
                </div>
                <button onClick={confirmPayment}
                  style={{padding:'10px 20px',background:'linear-gradient(135deg,#1b5e20,#388e3c)',color:'#fff',
                    border:'none',borderRadius:'8px',fontWeight:800,fontSize:'0.8rem',cursor:'pointer',
                    boxShadow:'0 2px 8px rgba(27,94,32,0.3)'}}>
                  ✅ செலுத்தியாச்சு — I have paid ₹{PAYMENT_AMOUNT}/-
                </button>
                <div style={{fontSize:'0.55rem',color:'#888',marginTop:'6px'}}>
                  After confirming, you can view data and save PDF for {selMonth} {selectedYearNum}
                </div>
              </div>

              {/* Cancel */}
              <button onClick={() => setPaymentState('needs_payment')}
                style={{padding:'6px 12px',background:'#eee',color:'#555',border:'1px solid #ccc',borderRadius:'6px',fontSize:'0.7rem',cursor:'pointer'}}>
                ← பின் செல்
              </button>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* DATA + PDF — show only when free/paid        */}
      {/* ═══════════════════════════════════════════ */}
      {!loading && !error && canShowData && selMonth && (
        <>
          {/* Month status + Results count */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px',flexWrap:'wrap',gap:'4px'}}>
            <span style={{fontSize:'0.75rem',color:T.greenDk,fontWeight:700}}>
              📊 {filtered.length} ச்ராத்த நாட்கள் — {selMonth} {selectedYearNum}
            </span>
            <span style={{fontSize:'0.55rem',color:'#888'}}>
              {loadTime > 0 && `⚡ ${(loadTime/1000).toFixed(1)}s`}
              {' '}
              {dataSource === 'vakya' ? '🔧 வாக்கிய இயந்திரம்' : dataSource === 'json' ? '📦 JSON' : dataSource === 'hybrid' ? '🔧+📦 Hybrid' : ''}
              {' '}
              {monthIsPast ? '🟢 இலவசம்' : monthIsCurrent ? '🟢 நடப்பு' : isAlreadyPaid ? '🔵 செலுத்தப்பட்டது' : ''}
            </span>
          </div>

          {/* PDF Save Button */}
          <div style={{textAlign:'right',marginBottom:'6px'}}>
            <button onClick={generatePDF}
              style={{padding:'6px 14px',background:'linear-gradient(135deg,#c62828,#ad1457)',color:'#fff',
                border:'none',borderRadius:'6px',fontWeight:700,fontSize:'0.7rem',cursor:'pointer',
                boxShadow:'0 2px 6px rgba(198,40,40,0.2)'}}>
              📄 PDF சேமி / அச்சிடு
            </button>
          </div>

          {/* Results Table */}
          {filtered.length > 0 && (
            <div style={{overflowX:'auto'}} ref={tableRef}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.65rem'}}>
                <thead>
                  <tr style={{background:T.greenDk,color:'#fff'}}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>தமிழ்</th>
                    <th style={thStyle}>திதி (முடிவு நேரம்)</th>
                    <th style={thStyle}>ச்ராத்த திதி</th>
                    <th style={thStyle}>பக்ஷம்</th>
                    <th style={{...thStyle,minWidth:'110px'}}>அபராஹ்ண பங்கீடு</th>
                    <th style={{...thStyle,minWidth:'90px'}}>காரணம்</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d, i) => {
                    const isKrishna = d.PakshamNameInTamil?.includes('கிருஷ்ண');
                    const isSoonya = d._engineShr?.includes('சூன்ய');
                    const isAthithi = d._engineShr?.includes('அதிதி');
                    const rowBg = isSoonya ? '#fff3e0' : isAthithi ? '#e3f2fd' : isKrishna ? '#f3e5f5' : i % 2 ? '#f5f5f5' : '#fff';
                    const rowClass = isSoonya ? 'soonya' : isAthithi ? 'athithi' : isKrishna ? 'krishna' : '';
                    return (
                      <tr key={i} style={{background: rowBg}} className={rowClass}>
                        <td style={tdStyle}>{i + 1}</td>
                        <td style={{...tdStyle,fontWeight:700,color:T.greenDk,whiteSpace:'nowrap',fontSize:'0.55rem'}}>
                          {formatDate(d.EnglishDate)}
                        </td>
                        <td style={{...tdStyle,fontSize:'0.55rem'}}>
                          {d.TamilMonth} {d.TamilDate}
                        </td>
                        <td style={{...tdStyle,fontSize:'0.55rem',color:'#333',whiteSpace:'nowrap'}}>
                          {buildThithiInfo(d)}
                        </td>
                        <td style={{...tdStyle,fontWeight:700,
                          color: isSoonya ? T.orange : isAthithi ? '#1565c0' : T.maroon}}>
                          {d._engineShr || d.ShraadhdhaThithiNameInTamil || ''}
                        </td>
                        <td style={{...tdStyle,textAlign:'center',fontSize:'0.55rem',
                          color: isKrishna ? '#6a1b9a' : T.greenDk, fontWeight:600}}>
                          {isKrishna ? '🌙' : '☀️'}
                        </td>
                        <td style={{...tdStyle,fontSize:'0.5rem',color:'#555',whiteSpace:'nowrap'}}>
                          {d._aparahnaShare || ''}
                        </td>
                        <td style={{...tdStyle,fontSize:'0.5rem',color:'#777'}}>
                          {d._reason || ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* No results */}
          {filtered.length === 0 && (
            <div style={{textAlign:'center',padding:'20px',color:'#888',fontSize:'0.8rem'}}>
              {selMonth} மாதத்தில் ச்ராத்த நாட்கள் இல்லை
            </div>
          )}

          {/* PDF button at bottom too */}
          {filtered.length > 0 && (
            <div style={{textAlign:'center',marginTop:'8px'}}>
              <button onClick={generatePDF}
                style={{padding:'8px 20px',background:'linear-gradient(135deg,#c62828,#ad1457)',color:'#fff',
                  border:'none',borderRadius:'8px',fontWeight:700,fontSize:'0.75rem',cursor:'pointer',
                  boxShadow:'0 2px 8px rgba(198,40,40,0.25)'}}>
                📄 PDF சேமி / அச்சிடு — Save as PDF
              </button>
            </div>
          )}
        </>
      )}

      {/* Pricing Info */}
      <div style={{marginTop:'12px',background:'#fff3e0',padding:'10px',borderRadius:'8px',border:'1px solid #ffcc80'}}>
        <div style={{fontWeight:800,fontSize:'0.8rem',color:T.orange,marginBottom:'6px'}}>💰 கட்டண விவரம் — Pricing</div>
        <div style={{fontSize:'0.65rem',color:'#555',lineHeight:1.6}}>
          <p>🟢 <b>கடந்த மாதங்கள்</b> — முற்றிலும் இலவசம் (Past months — FREE)</p>
          <p>🟢 <b>நடப்பு மாதம்</b> — இலவசம் (Current month — FREE)</p>
          <p>💰 <b>எதிர்கால மாதங்கள்</b> — ₹{PAYMENT_AMOUNT}/- ஒரு மாதத்திற்கு (Future — ₹{PAYMENT_AMOUNT}/month)</p>
          <p>📄 <b>PDF</b> — செலுத்திய / இலவச மாதங்களுக்கு PDF சேமிக்கலாம்</p>
        </div>
      </div>

      {/* Engine Rules */}
      <div style={{marginTop:'10px',background:T.cream,padding:'10px',borderRadius:'8px',border:'1px solid '+T.border}}>
        <div style={{fontWeight:800,fontSize:'0.8rem',color:T.greenDk,marginBottom:'6px'}}>📏 நிர்ணய விதிகள் — Engine Rules</div>
        <div style={{fontSize:'0.65rem',color:T.maroon,lineHeight:1.6}}>
          <p>🔹 <b>அபராஹ்ண வ்யாப்தி</b> — Aparahna = 3/5 to 4/5 of day. அதிக வ்யாப்தி கொண்ட திதி வெற்றி</p>
          <p>🔹 <b>இரட்டிப்பு</b> — இரண்டும் full aparahna → இரண்டாவது நாள் வெற்றி</p>
          <p>🔹 <b>மாத எல்லை</b> — Td 1-3, கடைசி 2 நாட்கள் → சூன்ய திதி (🟠)</p>
          <p>🔹 <b>அதிதி</b> — alternate thithi இல்லை → அதிதி (🔵)</p>
          <p>🔹 <b>திதி (முடிவு நேரம்)</b> — ThithiNazhigai → clock time from SuryaUdhayam</p>
          <p>🔹 திதி தெரியாவிடில் <b>அமாவாசை</b> / <b>கிருஷ்ண அஷ்டமி</b> நாளில் செய்யலாம்</p>
        </div>
      </div>

      {/* Tharpana reference images */}
      <div style={{display:'flex',gap:'6px',marginTop:'10px',overflowX:'auto'}}>
        <img src="/images/rituals/sama-pitru-tarpanam.jpg" alt="பித்ரு தர்ப்பணம்" style={{height:'60px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/sama-deva-tarpanam.jpg" alt="தேவ தர்ப்பணம்" style={{height:'60px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/sama-rishi-tarpanam.jpg" alt="ரிஷி தர்ப்பணம்" style={{height:'60px',borderRadius:'6px',border:'1px solid '+T.border}} />
      </div>
    </div>
  );
};

const thStyle = {padding:'5px',border:'1px solid #ccc',whiteSpace:'nowrap'};
const tdStyle = {padding:'4px',border:'1px solid #eee',textAlign:'center'};

export default ShraaddhaThithiMakerPage;
